import async from 'async';
import { UploadFile } from '@/renderer/components/SpacePage/compositions/uploadHandler';
import { FileInfo, TeamPathInfo } from '@/renderer/scripts/utils/upload/types';
import { IS_BROWSER } from '@/common/constant/index';
import { getConcatDirPath } from '@/renderer/scripts/utils/transmission/fileUtils';
import { FileElectronInfo } from '@/common/listFolders';

/**
 * @description 文件（夹）格式化
 * @param files 文件（夹）列表
 * @param currPath 云盘内路径
 * @param spaceId
 * @param teamPathInfo
 * @param routerName
 */
export const filesClassifyAdapter = async (
  files: Array<UploadFile | FileElectronInfo>,
  currPath: string,
  spaceId: string,
  routerName: string,
  teamPathInfo?: TeamPathInfo,
) => {
  // TODO loganylwu 格式化文件在浏览器和Node.js端分别有相似的逻辑，可优化
  if (IS_BROWSER) {
    return filesClassifyInBrowser(files as UploadFile[], currPath, spaceId, routerName, teamPathInfo);
  }
  return filesClassifyInElectron(files as FileElectronInfo[], currPath, spaceId, routerName, teamPathInfo);
};
/**
 * 浏览器内处理文件标准化，根据浏览器的文件对象结构进行处理，返回标准的文件对象<fileName,FileInfo>
 */
const filesClassifyInBrowser = async (
  files: UploadFile[],
  currPath: string,
  spaceId: string,
  routerName: string,
  teamPathInfo?: TeamPathInfo,
) => {
  const fileMap: Record<string, Array<FileInfo>> = {};
  const foldersMap = new Map<string, FileInfo>();
  // 整理文件格式
  await async.forEachLimit([...files], 16, (file, next) => {
    const fileContainer = file.raw ? file.raw : file;
    const { webkitRelativePath, fullPath, name, path: localPath, size, lastModified } = fileContainer;
    const relativePath = fullPath || webkitRelativePath || name;
    // 正确的path: 文件夹a/文件夹b/文件a
    // 错误的path: /文件夹a/文件夹b/文件a
    const path = relativePath.split('/').filter(item => item !== '');
    // 获取父文件夹（若根路径文件夹与文件名称不一致，就使用根路径文件夹）
    const rootFolderName = path.length > 1 ? path[0] : '';
    fileMap[rootFolderName || name] = fileMap[rootFolderName || name] || [];
    let formatName = name.trim();
    if (formatName.length > 200) {
      const ext = formatName.slice(formatName.lastIndexOf('.'));
      formatName = formatName.slice(0, 200 - ext.length) + ext;
    }
    path.pop();
    const fullFolderPath = path.join('/');
    let fullFolder: string = fullFolderPath.replace(/\/$/, '');
    const folders = fullFolder.split('/');
    for (let index = 0; index < folders.length; index++) {
      folders[index] = folders[index].trim();
      const element = folders[index];
      if (element.length > 200) {
        folders[index] = element.slice(0, 200);
      }
    }
    fullFolder = folders.join('/');
    const remoteFolder: string = getConcatDirPath(currPath, fullFolder);
    const filterFile: FileInfo = {
      name: formatName,
      localPath,
      rootFolderName: rootFolderName !== relativePath ? rootFolderName : '',
      remoteRootFolder: getConcatDirPath(currPath, rootFolderName),
      remoteFolder,
      remotePath: getConcatDirPath(remoteFolder, formatName),
      isDir: false,
      conflictResolutionStrategy: 'rename',
      size,
      mtime: lastModified,
      // 浏览器无法直接取到文件夹大小，需要重新计算
      folderSize: size,
      spaceId,
      teamPathInfo,
      routerName,
      body: file,
    };
    fileMap[rootFolderName || name].push(filterFile);

    // 处理文件夹
    if (fullFolder) {
      const _dirNames = fullFolder.split(/\/|\\/);
      const dirName = _dirNames[_dirNames.length - 1];
      _dirNames.pop();
      const formatRemoteFolder = _dirNames.join('/').replace(/\/$/, '');
      const dirInfo: FileInfo = {
        name: dirName,
        localPath: fullFolder,
        rootFolderName: filterFile.rootFolderName,
        remoteRootFolder: getConcatDirPath(currPath, rootFolderName),
        remoteFolder: formatRemoteFolder,
        remotePath: remoteFolder,
        isDir: true,
        conflictResolutionStrategy: 'rename',
        size: 0,
        mtime: 0,
        folderSize: 0,
        spaceId,
        routerName,
        teamPathInfo,
      };
      foldersMap.set(dirInfo.remotePath, dirInfo);

      // 将根目录页添加至 foldersMap 中 （当一级目录下无文件只有文件夹时，上述逻辑不会整理出根目录文件夹项，因此此处需要额外处理根目录文件夹）
      if (!foldersMap.get(rootFolderName)) {
        const remoteRootFolder = getConcatDirPath(currPath, rootFolderName);
        const rootFolderInfo: FileInfo = {
          name: rootFolderName,
          localPath: rootFolderName,
          rootFolderName,
          remoteRootFolder,
          remoteFolder: remoteRootFolder,
          remotePath: remoteRootFolder,
          isDir: true,
          conflictResolutionStrategy: 'rename',
          size: 0,
          mtime: 0,
          folderSize: 0,
          spaceId,
          routerName,
          teamPathInfo,
        };
        foldersMap.set(dirInfo.remotePath, rootFolderInfo);
      }
    }
    next();
  });

  // 将文件夹放入filemap 中
  await async.forEachLimit(
    [...foldersMap].map(v => v[1]),
    16,
    (folder: FileInfo, next) => {
      fileMap[folder.rootFolderName].push(folder);
      next();
    },
  );
  await async.forEachLimit(Object.values(fileMap), 16, (filterFiles: FileInfo[], next) => {
    let folderSize = 0;
    async
      .forEachLimit(Object.values(filterFiles), 16, (file, next) => {
        folderSize += file.size;
        next();
      })
      .then(() => {
        async
          .forEachLimit(Object.values(filterFiles), 16, (file, next) => {
            file.folderSize = folderSize;
            next();
          })
          .then();
      });
    next();
  });
  foldersMap.clear();
  return {
    fileMap,
  };
};

/**
 * 基于ELectron的PC端文件对象，进行标准化
 * @param files
 * @param currPath 云盘路径
 * @param spaceId
 * @param routerName
 * @param teamPathInfo
 */
const filesClassifyInElectron = async (
  files: FileElectronInfo[],
  currPath: string,
  spaceId: string,
  routerName: string,
  teamPathInfo?: TeamPathInfo,
) => {
  const fileMap: Record<string, Array<FileInfo>> = {};
  await async.forEachLimit(files, 16, function iterator(file, next) {
    const fileContainer = file.raw ? file.raw : file;
    const {
      isDir,
      name,
      path: localPath,
      size,
      folder: rootFolderName = '',
      mtime,
      folderSize,
      relativePath,
    } = fileContainer;
    fileMap[rootFolderName || name] = fileMap[rootFolderName || name] || [];

    // 本地路径中 正则表达式替换"/" "\" -> '/'
    const formatPath = localPath.replace(/\/|\\/g, '/');
    // 兼容windows的 / 和 \\ 路径分隔符 （？此处需要验证一下Mac端，因为Mac允许文件名前面有空格）
    const formatName = name.replace(/\/|\\/g, '/');
    // 简化的文件全名（不能超过200）
    let simpleFormatName = formatName;
    if (formatName.length > 200 && !isDir) {
      const ext = formatName.slice(formatName.lastIndexOf('.'));
      simpleFormatName = formatName.slice(0, 200 - ext.length) + ext;
    }
    const filterFile: FileInfo = {
      name: simpleFormatName,
      localPath,
      rootFolderName,
      remoteRootFolder: getConcatDirPath(currPath, rootFolderName),
      remoteFolder: '',
      remotePath: '',
      isDir: isDir || false,
      conflictResolutionStrategy: 'rename',
      size: size || 0,
      mtime: mtime || 0,
      folderSize: folderSize || size || 0,
      spaceId,
      routerName,
      teamPathInfo,
    };
    let remoteFolder = '';
    if (!rootFolderName) {
      // 单独文件
      remoteFolder = currPath;
    } else {
      // 通过绝对路径移除上层目录路径，从所上传的文件夹开始，避免因名称有包含关系引起的问题
      // 删除本地根路径，保留远程相对路径
      const relativeFilePath = formatPath.replace(relativePath as string, '').slice(1);
      // folderPath 可能为 很深层级的文件夹/3.1/3.1.1, 且每一层都可能会超过200
      const paths: Array<string> = relativeFilePath.split('/');
      paths.pop();
      const folderNameString: string = paths.join('/');
      const folderNames: Array<string> = folderNameString.split('/');
      for (let index = 0; index < folderNames.length; index++) {
        const folderName = folderNames[index]; // Mac 的文件夹允许前后有空格，因此此处不裁剪前后空格
        if (folderName.length > 200) {
          folderNames[index] = folderName.slice(0, 200);
        }
      }
      remoteFolder = getConcatDirPath(currPath, folderNames);
    }
    filterFile.remoteFolder = remoteFolder;
    filterFile.remotePath = getConcatDirPath(remoteFolder, simpleFormatName);
    fileMap[rootFolderName || name].push(filterFile);
    next();
  });
  return {
    fileMap,
  };
};
